import { useEffect, useRef } from "react"
import ContactBar from "../ContactBar"
import ThemeToggle from "../ThemeToggle"
import {
  LANDING_CUBE_CONFIG,
  LANDING_CUBE_EDGES,
  LANDING_CUBE_VERTICES,
  SITE_INFO,
} from "../../constants"
import {
  createCornerPulse,
  normalizePointerPosition,
  projectCubeVertices,
} from "../../utils"
import "../../styles/glitch.css"

const {
  BASE_STROKE_FALLBACK,
  CAMERA_DISTANCE,
  CUBE_VIEWPORT_RATIO,
  GLITCH_SHIFT_PX,
  HOVER_MAX_PITCH,
  HOVER_MAX_YAW,
  INERTIA_MAX_SPEED,
  INERTIA_MIN_SPEED,
  INERTIA_RELEASE_MAX_AGE_MS,
  MAX_PITCH,
  MAX_PIXEL_RATIO,
  ORBIT_SENSITIVITY,
  SPIN_DAMPING,
  SPIN_EPSILON,
} = LANDING_CUBE_CONFIG

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

export default function LandingPage() {
  const sectionRef = useRef(null)
  const canvasRef = useRef(null)

  useEffect(() => {
    const section = sectionRef.current
    const canvas = canvasRef.current
    if (!section || !canvas) {
      return undefined
    }

    const context = canvas.getContext("2d")
    if (!context) {
      return undefined
    }

    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    let frameId = null
    let cssWidth = 0
    let cssHeight = 0
    let pointerX = 0
    let pointerY = 0
    let baseYaw = 0
    let basePitch = 0
    let yawVelocity = 0
    let pitchVelocity = 0
    let isDragging = false
    let isHeroVisible = true
    let prefersReducedMotion = reducedMotionQuery.matches
    let activePointerId = null
    let dragStartClientX = 0
    let dragStartClientY = 0
    let dragStartYaw = 0
    let dragStartPitch = 0
    let dragLastClientX = 0
    let dragLastClientY = 0
    let dragLastTime = null
    let lastFrameTime = null
    let accentColor = BASE_STROKE_FALLBACK
    let cubeBounds = { minX: 0, maxX: 0, minY: 0, maxY: 0 }

    const updatePointer = (clientX, clientY) => {
      const nextPointer = normalizePointerPosition(
        clientX,
        clientY,
        window.innerWidth,
        window.innerHeight
      )
      pointerX = nextPointer.x
      pointerY = nextPointer.y
    }

    const isInsideCubeBounds = (event) => {
      const rect = canvas.getBoundingClientRect()
      const localX = event.clientX - rect.left
      const localY = event.clientY - rect.top
      return (
        localX >= cubeBounds.minX &&
        localX <= cubeBounds.maxX &&
        localY >= cubeBounds.minY &&
        localY <= cubeBounds.maxY
      )
    }

    const updateAccentColor = () => {
      accentColor =
        getComputedStyle(canvas).getPropertyValue("--accent-primary").trim() ||
        BASE_STROKE_FALLBACK
    }

    const drawCornerRadiation = (
      screenPoints,
      shiftX,
      shiftY,
      cornerPulse,
      strokeStyle,
      alpha
    ) => {
      context.save()
      context.globalCompositeOperation = "lighter"
      context.globalAlpha = alpha
      context.lineWidth = 1.15
      context.strokeStyle = strokeStyle
      context.translate(shiftX, shiftY)
      context.beginPath()

      LANDING_CUBE_EDGES.forEach(([startIndex, endIndex]) => {
        const start = screenPoints[startIndex]
        const end = screenPoints[endIndex]
        const dx = end.x - start.x
        const dy = end.y - start.y
        const edgeLength = Math.hypot(dx, dy) || 1
        const unitX = dx / edgeLength
        const unitY = dy / edgeLength
        const startLength = edgeLength * cornerPulse[startIndex]
        const endLength = edgeLength * cornerPulse[endIndex]

        context.moveTo(start.x, start.y)
        context.lineTo(start.x + unitX * startLength, start.y + unitY * startLength)
        context.moveTo(end.x, end.y)
        context.lineTo(end.x - unitX * endLength, end.y - unitY * endLength)
      })

      context.stroke()
      context.restore()
    }

    const drawCube = (timeStamp) => {
      context.clearRect(0, 0, cssWidth, cssHeight)

      const deltaSeconds =
        lastFrameTime === null
          ? 0
          : Math.min(0.05, Math.max(0, (timeStamp - lastFrameTime) / 1000))
      lastFrameTime = timeStamp

      if (!isDragging && deltaSeconds > 0) {
        baseYaw += yawVelocity * deltaSeconds
        basePitch = clamp(
          basePitch + pitchVelocity * deltaSeconds,
          -MAX_PITCH,
          MAX_PITCH
        )

        const damping = Math.exp(-SPIN_DAMPING * deltaSeconds)
        yawVelocity *= damping
        pitchVelocity *= damping

        if (Math.abs(yawVelocity) < SPIN_EPSILON) {
          yawVelocity = 0
        }
        if (Math.abs(pitchVelocity) < SPIN_EPSILON) {
          pitchVelocity = 0
        }
      }

      const hoverYaw = isDragging ? 0 : pointerX * HOVER_MAX_YAW
      const hoverPitch = isDragging ? 0 : pointerY * HOVER_MAX_PITCH
      const { bounds, screenPoints } = projectCubeVertices(
        LANDING_CUBE_VERTICES,
        baseYaw + hoverYaw,
        basePitch + hoverPitch,
        CAMERA_DISTANCE,
        cssWidth,
        cssHeight,
        CUBE_VIEWPORT_RATIO
      )
      cubeBounds = bounds

      const animationTime = isDragging || yawVelocity !== 0 || pitchVelocity !== 0 ? timeStamp : 0
      const redShiftX = Math.sin(animationTime * 0.02) * GLITCH_SHIFT_PX
      const redShiftY = Math.cos(animationTime * 0.017) * GLITCH_SHIFT_PX * 0.35
      const greenShiftX = -Math.cos(animationTime * 0.018) * GLITCH_SHIFT_PX
      const greenShiftY = Math.sin(animationTime * 0.021) * GLITCH_SHIFT_PX * 0.35
      const redCornerPulse = createCornerPulse(
        LANDING_CUBE_VERTICES.length,
        animationTime,
        0.004,
        0.95
      )
      const greenCornerPulse = createCornerPulse(
        LANDING_CUBE_VERTICES.length,
        animationTime,
        0.0047,
        1.11,
        Math.PI / 3
      )

      context.beginPath()
      LANDING_CUBE_EDGES.forEach(([startIndex, endIndex]) => {
        context.moveTo(screenPoints[startIndex].x, screenPoints[startIndex].y)
        context.lineTo(screenPoints[endIndex].x, screenPoints[endIndex].y)
      })

      context.lineWidth = 1.5
      context.strokeStyle = accentColor
      context.globalAlpha = 0.68
      context.stroke()

      drawCornerRadiation(
        screenPoints,
        redShiftX,
        redShiftY,
        redCornerPulse,
        "rgba(255, 70, 70, 0.9)",
        0.5
      )
      drawCornerRadiation(
        screenPoints,
        greenShiftX,
        greenShiftY,
        greenCornerPulse,
        "rgba(90, 255, 120, 0.9)",
        0.44
      )

      context.globalAlpha = 1

      if (
        isHeroVisible &&
        !prefersReducedMotion &&
        (isDragging || yawVelocity !== 0 || pitchVelocity !== 0)
      ) {
        frameId = window.requestAnimationFrame(drawFrame)
      }
    }

    const drawFrame = (timeStamp) => {
      frameId = null
      drawCube(timeStamp)
    }

    const scheduleDraw = () => {
      if (!isHeroVisible || frameId !== null) {
        return
      }
      frameId = window.requestAnimationFrame(drawFrame)
    }

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect()
      const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO)
      const nextWidth = Math.max(1, Math.round(rect.width * pixelRatio))
      const nextHeight = Math.max(1, Math.round(rect.height * pixelRatio))
      cssWidth = rect.width
      cssHeight = rect.height

      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth
        canvas.height = nextHeight
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      }

      scheduleDraw()
    }

    const startDrag = (event) => {
      if (event.pointerType === "touch" && event.cancelable) {
        event.preventDefault()
      }

      updatePointer(event.clientX, event.clientY)
      const currentYaw = baseYaw + pointerX * HOVER_MAX_YAW
      const currentPitch = basePitch + pointerY * HOVER_MAX_PITCH
      isDragging = true
      activePointerId = event.pointerId
      dragStartClientX = event.clientX
      dragStartClientY = event.clientY
      dragStartYaw = currentYaw
      dragStartPitch = currentPitch
      dragLastClientX = event.clientX
      dragLastClientY = event.clientY
      dragLastTime = event.timeStamp
      baseYaw = currentYaw
      basePitch = currentPitch
      yawVelocity = 0
      pitchVelocity = 0

      if (canvas.setPointerCapture) {
        canvas.setPointerCapture(event.pointerId)
      }
    }

    const onPointerMove = (event) => {
      const coalescedEvents = event.getCoalescedEvents?.()
      const sample = coalescedEvents?.[coalescedEvents.length - 1] || event
      updatePointer(sample.clientX, sample.clientY)

      if (isDragging && event.pointerId === activePointerId) {
        if (event.pointerType === "touch" && event.cancelable) {
          event.preventDefault()
        }

        baseYaw = dragStartYaw + (sample.clientX - dragStartClientX) * ORBIT_SENSITIVITY
        basePitch = clamp(
          dragStartPitch + (sample.clientY - dragStartClientY) * ORBIT_SENSITIVITY,
          -MAX_PITCH,
          MAX_PITCH
        )

        if (dragLastTime !== null) {
          const deltaSeconds = Math.max(0.01, (sample.timeStamp - dragLastTime) / 1000)
          yawVelocity =
            ((sample.clientX - dragLastClientX) * ORBIT_SENSITIVITY) / deltaSeconds
          pitchVelocity =
            ((sample.clientY - dragLastClientY) * ORBIT_SENSITIVITY) / deltaSeconds
        }

        dragLastClientX = sample.clientX
        dragLastClientY = sample.clientY
        dragLastTime = sample.timeStamp
      }

      scheduleDraw()
    }

    const onPointerDown = (event) => {
      if (!isInsideCubeBounds(event)) {
        return
      }

      startDrag(event)
      scheduleDraw()
    }

    const onPointerUp = (event) => {
      if (!isDragging || event.pointerId !== activePointerId) {
        return
      }

      const timeSinceLastMove =
        dragLastTime === null ? Number.POSITIVE_INFINITY : event.timeStamp - dragLastTime
      const orbitSpeed = Math.hypot(yawVelocity, pitchVelocity)
      const shouldKeepInertia =
        !prefersReducedMotion &&
        timeSinceLastMove <= INERTIA_RELEASE_MAX_AGE_MS &&
        orbitSpeed >= INERTIA_MIN_SPEED

      if (!shouldKeepInertia) {
        yawVelocity = 0
        pitchVelocity = 0
      } else if (orbitSpeed > INERTIA_MAX_SPEED) {
        const speedScale = INERTIA_MAX_SPEED / orbitSpeed
        yawVelocity *= speedScale
        pitchVelocity *= speedScale
      }

      isDragging = false
      activePointerId = null
      dragLastTime = null

      if (canvas.releasePointerCapture && canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId)
      }

      scheduleDraw()
    }

    const onPointerLeave = () => {
      if (isDragging) {
        return
      }

      pointerX = 0
      pointerY = 0
      scheduleDraw()
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (frameId !== null) {
          window.cancelAnimationFrame(frameId)
          frameId = null
        }
        lastFrameTime = null
        return
      }

      scheduleDraw()
    }

    const onReducedMotionChange = (event) => {
      prefersReducedMotion = event.matches
      if (prefersReducedMotion) {
        yawVelocity = 0
        pitchVelocity = 0
      }
      scheduleDraw()
    }

    updateAccentColor()
    resizeCanvas()

    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resizeCanvas)
    resizeObserver?.observe(canvas)
    if (!resizeObserver) {
      window.addEventListener("resize", resizeCanvas)
    }

    const intersectionObserver =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(([entry]) => {
            isHeroVisible = entry?.isIntersecting ?? true
            section.dataset.visible = String(isHeroVisible)
            lastFrameTime = null

            if (!isHeroVisible && frameId !== null) {
              window.cancelAnimationFrame(frameId)
              frameId = null
            } else if (isHeroVisible) {
              scheduleDraw()
            }
          })
    intersectionObserver?.observe(section)

    const themeObserver = new MutationObserver(() => {
      updateAccentColor()
      scheduleDraw()
    })
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    })

    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("pointerup", onPointerUp)
    window.addEventListener("pointercancel", onPointerUp)
    section.addEventListener("pointermove", onPointerMove)
    section.addEventListener("pointerleave", onPointerLeave)
    canvas.addEventListener("pointerdown", onPointerDown)
    reducedMotionQuery.addEventListener?.("change", onReducedMotionChange)

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
      resizeObserver?.disconnect()
      intersectionObserver?.disconnect()
      themeObserver.disconnect()
      if (!resizeObserver) {
        window.removeEventListener("resize", resizeCanvas)
      }
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerUp)
      section.removeEventListener("pointermove", onPointerMove)
      section.removeEventListener("pointerleave", onPointerLeave)
      canvas.removeEventListener("pointerdown", onPointerDown)
      reducedMotionQuery.removeEventListener?.("change", onReducedMotionChange)
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      id="landing"
      className="landing-hero relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 text-center"
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 z-0 h-full w-full cursor-grab touch-pan-y active:cursor-grabbing"
      />
      <div className="relative z-10 flex flex-col items-center">
        <h1
          className="glitchHeading mb-6 whitespace-nowrap font-heading text-[clamp(2.2rem,5vw,3.2rem)]"
          data-text={SITE_INFO.name}
        >
          {SITE_INFO.name}
        </h1>
        <ContactBar />
        <ThemeToggle className="mt-5" />
      </div>
    </section>
  )
}
